import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../../contexts/AdminContext';
import AdminLayout from '../../components/admin/AdminLayout';
import ImageUpload from '../../components/admin/ImageUpload';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminJourney = () => {
  const { getAuthHeaders } = useAdmin();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const response = await axios.get(`${API}/admin/journey`, getAuthHeaders());
      setStories(response.data);
    } catch (error) {
      console.error('Error fetching stories:', error);
    }
    setLoading(false);
  };

  const handleEdit = (story) => {
    setEditing(story.id);
    setFormData(story);
  };

  const handleSave = async () => {
    try {
      const dataToSave = { ...formData };
      if (!dataToSave.id || dataToSave.id === 'new') {
        dataToSave.id = crypto.randomUUID();
      }
      
      if (editing === 'new') {
        await axios.post(`${API}/admin/journey`, dataToSave, getAuthHeaders());
      } else {
        await axios.put(`${API}/admin/journey/${formData.id}`, dataToSave, getAuthHeaders());
      }
      await fetchStories();
      setEditing(null);
      setFormData({});
      alert('Story saved successfully!');
    } catch (error) {
      console.error('Error saving story:', error);
      alert('Error saving story: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this story?')) {
      try {
        await axios.delete(`${API}/admin/journey/${id}`, getAuthHeaders());
        await fetchStories();
        alert('Story deleted successfully!');
      } catch (error) {
        console.error('Error deleting story:', error);
        alert('Error deleting story');
      }
    }
  };

  const handleNew = () => {
    setEditing('new');
    setFormData({
      id: '',
      title: '',
      date: '',
      impact: '',
      image: ''
    });
  };

  if (loading) return <AdminLayout><p className="text-white">Loading...</p></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="display-medium text-white">Journey Stories Management</h1>
          <button onClick={handleNew} className="btn-primary flex items-center gap-2">
            <Plus size={20} /> Add New Story
          </button>
        </div>

        {editing && (
          <div className="bg-white/10 border border-white/25 p-6 rounded-lg">
            <h2 className="heading-2 text-white mb-4">{editing === 'new' ? 'New Story' : 'Edit Story'}</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="body-small text-white mb-1 block">Title</label>
                  <input
                    type="text"
                    value={formData.title || ''}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                    placeholder="e.g., Tech Unwind - 50 Attendees"
                  />
                </div>
                <div>
                  <label className="body-small text-white mb-1 block">Date</label>
                  <input
                    type="text"
                    value={formData.date || ''}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                    placeholder="e.g., Dec 2025"
                  />
                </div>
              </div>
              <div>
                <label className="body-small text-white mb-1 block">Impact Story</label>
                <textarea
                  value={formData.impact || ''}
                  onChange={(e) => setFormData({ ...formData, impact: e.target.value })}
                  className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded h-24"
                  placeholder="Describe the impact of this event"
                />
              </div>
              <div>
                <ImageUpload
                  label="Story Image"
                  value={formData.image}
                  onChange={(v) => setFormData({ ...formData, image: v })}
                  testId="journey-image"
                />
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button onClick={handleSave} className="btn-primary flex items-center gap-2">
                <Save size={20} /> Save
              </button>
              <button onClick={() => { setEditing(null); setFormData({}); }} className="btn-secondary flex items-center gap-2">
                <X size={20} /> Cancel
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {stories.map((story) => (
            <div key={story.id} className="bg-white/5 border border-white/25 overflow-hidden rounded-lg">
              {story.image && (
                <img src={story.image} alt={story.title} className="w-full h-48 object-cover" />
              )}
              <div className="p-6">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <p className="body-small text-[#00FFD1] mb-1">{story.date}</p>
                    <h3 className="heading-3 text-white mb-2">{story.title}</h3>
                    <p className="body-small text-white/85">{story.impact}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleEdit(story)} className="text-[#00FFD1] hover:text-[#6FD2C0]">
                      <Edit size={20} />
                    </button>
                    <button onClick={() => handleDelete(story.id)} className="text-red-500 hover:text-red-400">
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminJourney;
